/**
 * Skills Library panel for the Browser-Use admin console.
 *
 * Lists the 5 in-code skills, lets the operator pick one, dynamically
 * renders parameter inputs from the skill's schema, and submits via
 * /api/skills/{id}/run. Credentials selection is filtered to the
 * skill's vendor automatically.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, Play, ShieldCheck, ShieldAlert, AlertTriangle, Lock,
  X, Eye, ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const SAFETY_COLORS = {
  read_only: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  mutation: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  financial: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  compliance: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
};
const SAFETY_ICON = {
  read_only: <ShieldCheck className="w-3.5 h-3.5" />,
  mutation: <AlertTriangle className="w-3.5 h-3.5" />,
  financial: <ShieldAlert className="w-3.5 h-3.5" />,
  compliance: <Lock className="w-3.5 h-3.5" />,
};

export default function SkillsPanel({ credentials, onJobSubmitted, onError }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [credLabel, setCredLabel] = useState('');
  const [running, setRunning] = useState(false);
  const [recentRuns, setRecentRuns] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, rRes] = await Promise.all([
        fetch(`${API}/api/skills/list`),
        fetch(`${API}/api/skills/runs?limit=20`),
      ]);
      setSkills((await sRes.json()).skills || []);
      setRecentRuns((await rRes.json()).runs || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSkill = (skill) => {
    setSelectedSkill(skill);
    const defaults = {};
    (skill.parameters || []).forEach((p) => {
      if (p.default !== null && p.default !== undefined) defaults[p.name] = p.default;
    });
    setParamValues(defaults);
    // Auto-pick the first matching credential if any
    const match = (credentials || []).find((c) => c.vendor === skill.vendor);
    setCredLabel(match ? match.label : '');
  };

  const closeModal = () => {
    setSelectedSkill(null);
    setParamValues({});
    setCredLabel('');
  };

  const submitRun = async () => {
    if (!selectedSkill) return;
    setRunning(true);
    try {
      // Strip out cred-placeholder defaults — server resolves them via vault
      const cleanParams = {};
      Object.entries(paramValues).forEach(([k, v]) => {
        if (typeof v === 'string' && v.startsWith('{{cred:')) return;
        if (v !== '' && v !== undefined && v !== null) cleanParams[k] = v;
      });
      const body = {
        params: cleanParams,
        initiator: 'admin-console',
        credentials_label: credLabel || null,
      };
      const r = await fetch(`${API}/api/skills/${selectedSkill.id}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'run failed');
      if (onJobSubmitted) onJobSubmitted(j);
      closeModal();
      await load();
    } catch (e) {
      if (onError) onError(e.message);
    }
    setRunning(false);
  };

  if (loading) return <div className="text-zinc-500 py-12 text-center">Loading skills…</div>;

  return (
    <>
      <div className="text-xs text-zinc-500 mb-3">
        Pre-baked workflows that wrap Browser-Use with parameter schemas, credentials, and Critic safety class.
        Each skill is the same as submitting a hand-crafted task — but version-controlled, audited, and reusable across hotels.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {skills.map((s) => (
          <Card key={s.id} className="bg-zinc-900/60 border-zinc-800" data-testid={`skill-card-${s.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-100">{s.name}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">vendor · <span className="font-mono">{s.vendor}</span></div>
                </div>
                <Badge className={SAFETY_COLORS[s.safety_class] || ''}>
                  <span className="inline-flex items-center gap-1">{SAFETY_ICON[s.safety_class]} {s.safety_class}</span>
                </Badge>
              </div>
              <p className="text-xs text-zinc-400 mb-3 line-clamp-3">{s.description}</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {(s.tags || []).map((t) => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">{t}</span>
                ))}
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
                  {(s.parameters || []).length} params · {s.max_steps} max steps
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => openSkill(s)}
                className="w-full bg-amber-600 hover:bg-amber-500"
                data-testid={`skill-run-${s.id}-btn`}
              >
                <Play className="w-4 h-4 mr-1" /> Run skill
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent skill runs */}
      <div className="text-xs text-zinc-500 mb-2">Recent skill runs</div>
      {recentRuns.length === 0 ? (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="py-6 text-center text-zinc-500 text-sm">
            No skill runs yet. Pick a skill above to run one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {recentRuns.map((r) => (
            <Card key={r.id} className="bg-zinc-900/60 border-zinc-800" data-testid={`skill-run-row-${r.id}`}>
              <CardContent className="p-2.5">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <span className="font-mono text-amber-300">{r.metadata?.skill_id}</span>
                    <ChevronRight className="w-3 h-3 text-zinc-600" />
                    <Badge className={SAFETY_COLORS[r.metadata?.safety_class] || 'bg-zinc-800'}>
                      {r.metadata?.safety_class}
                    </Badge>
                    <span className="text-zinc-500 truncate">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <Badge className={
                    r.status === 'completed' ? 'bg-emerald-500/15 text-emerald-300' :
                    r.status === 'failed' ? 'bg-rose-500/15 text-rose-300' :
                    r.status === 'blocked_by_critic' ? 'bg-amber-500/15 text-amber-300' :
                    'bg-blue-500/15 text-blue-300'
                  }>{r.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal: parameter form */}
      {selectedSkill && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center px-4 py-8 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          data-testid="skill-run-modal"
        >
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-lg font-semibold">{selectedSkill.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {selectedSkill.vendor} · {selectedSkill.max_steps} max steps · <Badge className={SAFETY_COLORS[selectedSkill.safety_class]}>{selectedSkill.safety_class}</Badge>
                </div>
              </div>
              <button onClick={closeModal} className="text-zinc-500 hover:text-zinc-200" data-testid="skill-modal-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-zinc-400 mb-4">{selectedSkill.description}</p>

            {selectedSkill.credentials_label_hint !== null && (
              <div className="mb-3">
                <label className="text-xs text-zinc-500 mb-1 block">
                  Vendor credentials (encrypted vault)
                </label>
                <select
                  value={credLabel}
                  onChange={(e) => setCredLabel(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-2 text-sm"
                  data-testid="skill-cred-select"
                >
                  <option value="">— none —</option>
                  {(credentials || [])
                    .filter((c) => c.vendor === selectedSkill.vendor)
                    .map((c) => (
                      <option key={c.label} value={c.label}>{c.vendor}/{c.label}</option>
                    ))}
                </select>
                {(credentials || []).filter((c) => c.vendor === selectedSkill.vendor).length === 0 && (
                  <div className="text-[10px] text-rose-300 mt-1">
                    No credentials stored for vendor "{selectedSkill.vendor}" — add one in the Vendor credentials tab.
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {(selectedSkill.parameters || []).map((p) => {
                const isCredPlaceholder = typeof p.default === 'string' && p.default.startsWith('{{cred:');
                if (isCredPlaceholder) return null;
                return (
                  <div key={p.name}>
                    <label className="text-xs text-zinc-400 mb-0.5 block">
                      {p.name}
                      {p.required && <span className="text-rose-400"> *</span>}
                      <span className="text-zinc-600 ml-2 text-[10px]">{p.type}</span>
                    </label>
                    <input
                      type={p.type === 'integer' ? 'number' : p.type === 'date' ? 'date' : 'text'}
                      value={paramValues[p.name] ?? ''}
                      onChange={(e) => setParamValues({ ...paramValues, [p.name]: e.target.value })}
                      placeholder={p.example ? `e.g. ${p.example}` : p.description}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-sm"
                      data-testid={`skill-param-${p.name}-input`}
                    />
                    {p.description && (
                      <div className="text-[10px] text-zinc-600 mt-0.5">{p.description}</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-zinc-800">
              <a
                href={selectedSkill.target_url}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-zinc-500 hover:text-amber-300 flex items-center gap-1"
              >
                <Eye className="w-3 h-3" /> target site
              </a>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={closeModal} data-testid="skill-modal-cancel-btn">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={submitRun}
                  disabled={running}
                  className="bg-amber-600 hover:bg-amber-500"
                  data-testid="skill-modal-run-btn"
                >
                  {running ? 'Running…' : <>Run · Critic-gated <Play className="w-3 h-3 ml-1" /></>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
